/**
 * Business Bible Widget
 *
 * Embeddable widget for Shopify stores that provides AI-powered Biblical business advice.
 * No dependencies - pure vanilla JavaScript.
 */

(function() {
    'use strict';

    // Configuration
    const CONFIG = {
        apiUrl: window.BUSINESS_BIBLE_API_URL || 'https://gw.busbible.com/chat',
        questionsUrl: window.BUSINESS_BIBLE_QUESTIONS_URL || 'https://gw.busbible.com/questions.json',
        backgroundUrl: window.BUSINESS_BIBLE_BACKGROUND_URL || 'https://gw.busbible.com/background.png',
        widgetId: 'business-bible-widget'
    };

    // Apply background image dynamically if configured
    if (window.BUSINESS_BIBLE_BACKGROUND_URL) {
        document.documentElement.style.setProperty('--bb-background-image', `url('${CONFIG.backgroundUrl}')`);
    }

    // State
    let defaultQuestions = [];
    let currentQuestionIndex = -1;
    let isLoading = false;

    /**
     * Load default questions from JSON file
     */
    async function loadQuestions() {
        try {
            const response = await fetch(CONFIG.questionsUrl);
            const data = await response.json();
            defaultQuestions = data.defaultQuestions || [];
        } catch (error) {
            console.error('Failed to load questions:', error);
            // Fallback questions if fetch fails
            defaultQuestions = [
                "What does the Bible say about business?",
                "How does business affect my life?",
                "What is my purpose?"
            ];
        }
    }

    /**
     * Filter questions based on input
     */
    function filterQuestions(input) {
        if (!input) return defaultQuestions;
        const lowerInput = input.toLowerCase();
        return defaultQuestions.filter(q =>
            q.toLowerCase().includes(lowerInput)
        );
    }

    /**
     * Show autocomplete suggestions
     */
    function showAutocomplete(input, suggestions) {
        const container = document.getElementById('bb-autocomplete');
        if (!suggestions || suggestions.length === 0 || !input) {
            container.style.display = 'none';
            return;
        }

        container.innerHTML = '';
        suggestions.forEach((suggestion, index) => {
            const item = document.createElement('div');
            item.className = 'bb-autocomplete-item';
            item.textContent = suggestion;
            item.setAttribute('data-index', index);

            item.addEventListener('click', () => {
                const inputEl = document.getElementById('bb-input');
                inputEl.value = suggestion;
                container.style.display = 'none';
                currentQuestionIndex = -1;
            });

            container.appendChild(item);
        });

        container.style.display = 'block';
    }

    /**
     * Hide autocomplete
     */
    function hideAutocomplete() {
        const container = document.getElementById('bb-autocomplete');
        container.style.display = 'none';
        currentQuestionIndex = -1;
    }

    /**
     * Navigate autocomplete with keyboard
     */
    function navigateAutocomplete(direction) {
        const items = document.querySelectorAll('.bb-autocomplete-item');
        if (items.length === 0) return;

        // Remove previous selection
        if (currentQuestionIndex >= 0 && currentQuestionIndex < items.length) {
            items[currentQuestionIndex].classList.remove('bb-autocomplete-selected');
        }

        // Update index
        if (direction === 'down') {
            currentQuestionIndex = (currentQuestionIndex + 1) % items.length;
        } else if (direction === 'up') {
            currentQuestionIndex = currentQuestionIndex <= 0 ? items.length - 1 : currentQuestionIndex - 1;
        }

        // Add new selection
        items[currentQuestionIndex].classList.add('bb-autocomplete-selected');

        // Update input value
        const inputEl = document.getElementById('bb-input');
        inputEl.value = items[currentQuestionIndex].textContent;
    }

    /**
     * Show loading state
     */
    function showLoading() {
        isLoading = true;
        const button = document.getElementById('bb-submit-btn');
        const responseEl = document.getElementById('bb-response');
        const widget = document.querySelector('.bb-widget');

        button.disabled = true;
        button.textContent = 'THINKING...';

        responseEl.innerHTML = '<div class="bb-loading"><div class="bb-spinner"></div></div>';
        responseEl.style.display = 'block';

        // Add class to reposition form
        widget.classList.add('bb-has-response');
    }

    /**
     * Hide loading state
     */
    function hideLoading() {
        isLoading = false;
        const button = document.getElementById('bb-submit-btn');
        button.disabled = false;
        button.textContent = 'ASK AI';
    }

    /**
     * Show error message
     */
    function showError(message) {
        const responseEl = document.getElementById('bb-response');
        responseEl.innerHTML = `<div class="bb-error">${message}</div>`;
        responseEl.style.display = 'block';
    }

    /**
     * Show AI response
     */
    function showResponse(text) {
        const responseEl = document.getElementById('bb-response');
        const htmlContent = parseMarkdown(text);
        responseEl.innerHTML = `<div class="bb-ai-response">${htmlContent}</div>`;
        responseEl.style.display = 'block';
    }

    /**
     * Escape HTML to prevent XSS
     */
    function escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Parse markdown to HTML
     */
    function parseMarkdown(text) {
        // Escape HTML first to prevent XSS
        let html = escapeHtml(text);

        // Bold: **text** or __text__
        html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

        // Italic: *text* or _text_
        html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
        html = html.replace(/_(.+?)_/g, '<em>$1</em>');

        // Line breaks
        html = html.replace(/\n/g, '<br>');

        // Numbered lists with brackets [1], [2], etc - convert to superscript
        html = html.replace(/\[(\d+)\]/g, '<sup>[$1]</sup>');

        return html;
    }

    /**
     * Send chat request to API
     */
    async function sendChatRequest(prompt) {
        try {
            const response = await fetch(CONFIG.apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ prompt })
            });

            if (!response.ok) {
                if (response.status === 429) {
                    throw new Error('Too many requests. Please wait a moment and try again.');
                } else if (response.status === 503) {
                    throw new Error('Service temporarily unavailable. Please try again later.');
                } else {
                    throw new Error('Failed to get response. Please try again.');
                }
            }

            const data = await response.json();
            return data.reply;
        } catch (error) {
            if (error.message.includes('fetch')) {
                throw new Error('Unable to connect to service. Please check your internet connection.');
            }
            throw error;
        }
    }

    /**
     * Handle form submission
     */
    async function handleSubmit(event) {
        if (event) event.preventDefault();
        if (isLoading) return;

        const inputEl = document.getElementById('bb-input');
        const prompt = inputEl.value.trim();

        if (!prompt) {
            showError('Please enter a question.');
            return;
        }

        hideAutocomplete();
        showLoading();

        try {
            const reply = await sendChatRequest(prompt);
            hideLoading();
            showResponse(reply);
        } catch (error) {
            hideLoading();
            showError(error.message);
        }
    }

    /**
     * Initialize the widget
     */
    async function init() {
        const container = document.getElementById(CONFIG.widgetId);
        if (!container) {
            console.error('Business Bible Widget: Container element not found');
            return;
        }

        // Load questions
        await loadQuestions();

        // Detect if mobile
        const isMobile = window.innerWidth <= 768;
        const inputElement = isMobile
            ? `<textarea
                id="bb-input"
                class="bb-input"
                placeholder="Ask a question..."
                maxlength="500"
                rows="4"
            ></textarea>`
            : `<input
                type="text"
                id="bb-input"
                class="bb-input"
                placeholder="Ask a question..."
                autocomplete="off"
                maxlength="500"
            />`;

        // Create widget HTML
        container.innerHTML = `
            <div class="bb-widget">
                <form id="bb-form" class="bb-form">
                    <div class="bb-input-container">
                        ${inputElement}
                        <div id="bb-autocomplete" class="bb-autocomplete"></div>
                    </div>
                    <button type="submit" id="bb-submit-btn" class="bb-button">ASK AI</button>
                </form>
                <div id="bb-response" class="bb-response"></div>
            </div>
        `;

        // Add event listeners
        const form = document.getElementById('bb-form');
        const inputEl = document.getElementById('bb-input');

        form.addEventListener('submit', handleSubmit);

        inputEl.addEventListener('input', (e) => {
            const value = e.target.value;
            const suggestions = filterQuestions(value);
            showAutocomplete(value, suggestions);
        });

        inputEl.addEventListener('keydown', (e) => {
            const autocomplete = document.getElementById('bb-autocomplete');
            const isVisible = autocomplete.style.display === 'block';

            if (isVisible) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    navigateAutocomplete('down');
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    navigateAutocomplete('up');
                } else if (e.key === 'Escape') {
                    hideAutocomplete();
                } else if (e.key === 'Enter' && currentQuestionIndex >= 0) {
                    e.preventDefault();
                    hideAutocomplete();
                    handleSubmit();
                }
            }
        });

        inputEl.addEventListener('focus', (e) => {
            const value = e.target.value;
            if (value) {
                const suggestions = filterQuestions(value);
                showAutocomplete(value, suggestions);
            }
        });

        // Close autocomplete when clicking outside
        document.addEventListener('click', (e) => {
            if (!e.target.closest('.bb-input-container')) {
                hideAutocomplete();
            }
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
